import React from "react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import { MANAGEMENT_USER_LICENSE_SEARCH, MYPAGE_ROUTE } from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { CrossCircleIcon } from "../components/Icons";
import { ScrollToTop } from "../components/ScrollToTop";
import { User } from "../types/User";
import i18n from "../i18n/configs";
import { OnlyMasterUserAuthorized } from "../components/OnlyMasterUserAuthorized";
import {
    convertUtcToLocalDate,
    formatDateTime,
    formatDateTimeJAFormat,
    formatUtcToLocalDateTime,
    formatUtcToLocalDateTimeJAFormat,
} from "../utils/DateUtils";
import { Product } from "../types/Product";
import { getProduct } from "../clients/ProductClient";
import { PaymentHistoryWithLicenses } from "../types/PaymentHistoryWithLicenses";
import { checkoutWithoutPayment } from "../clients/PaymentClient";
import { License } from "../types/License";
import { getPaymentHistory } from "../clients/PaymentHistoryClient";
import { PaymentHistory } from "../types/PaymentHistory";
import { productTypeOptions, licenseOptions } from "../consts/constants";
import MyDatePicker from "../components/MyDatePicker";
import { calcExpirationDate } from "../utils/LicenseUtils";

interface LocationState {
    selectedUser: User;
    selectedLicense: License;
}

/** 有効期限を加算するか | 利用開始日を再設定するか */
type Mode = "addExpiry" | "resetStart";

/**
 * 管理者向け - ライセンス更新画面
 * @returns JSX.Element
 */
const ManagementUserLicenseUpdate = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, getRecentToken } = useUserContext();
    const location = useLocation();
    const { selectedUser, selectedLicense } = location.state as LocationState;
    const [errorMessageTop, setErrorMessageTop] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");

    // ライセンス情報
    const [selectedLicenseId, setSelectedLicenseId] = useState<number>(selectedLicense.license_id);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [selectedProductType, setSelectedProductType] = useState<number>(11);
    const [selectedLicenseType, setSelectedLicenseType] = useState<number | undefined>(undefined);
    const [selectedActivationDate, setSelectedActivationDate] = useState<Date | null>(
        new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0),
    );
    const [selectedExpirationDate, setSelectedExpirationDate] = useState<Date | null>(
        new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0),
    );
    const [updatedLicense, setUpdatedLicense] = useState<PaymentHistoryWithLicenses | undefined>();

    // ページ制御
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState<Mode>("addExpiry");
    const [disableExpirationDate, setDisableExpirationDate] = useState<boolean>(false);
    const [updateCompleted, setUpdateCompleted] = useState<boolean>(false);

    /**
     * 処理中フラグ
     * ※多重クリックを防止するため、外部通信を伴うイベント処理では必ず使用すること
     */
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    // ページ表示時にuseEffectが2回実行されるのを防ぐために使用。
    // ※React.StrictModeの仕様
    const effectRun = useRef(false);

    // ページ初期化処理（DBから情報の取得）
    useEffect(() => {
        if (!user) {
            // ページリロード時はコンテキストも初期化されるため、空の場合は一度リターンする
            // コンテキストが生成されユーザー情報が生成された時点で再実行する
            return;
        }
        if (!effectRun.current) {
            fetchRecentPaymentInfo();
            return () => {
                effectRun.current = true;
            };
        }
    }, [user]);

    /**
     * 前回購入情報の取得処理
     * @returns
     */
    async function fetchRecentPaymentInfo() {
        setLoading(true);
        setErrorMessageTop("");
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessageTop(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API]購入履歴情報の取得
            const paymentHistoryResponse: PaymentHistory = await getPaymentHistory(
                idToken,
                selectedLicense.payment_history_id,
            );
            // [API]商品情報の取得
            const productResponse: Product = await getProduct(idToken, paymentHistoryResponse.product_id);
            setSelectedProduct(productResponse);
            setSelectedProductType(productResponse.product_type_id); // 商品タイプ決定
            setSelectedLicenseType(productResponse.license_type_id); // ライセンスタイプ決定
        } catch (error: any) {
            setErrorMessageTop(t("ErrorMessage.systemErrorMessage"));
        } finally {
            setLoading(false);
        }
    }

    /**
     * 有効期限指定モードを変更したときに利用開始日を再セットするフック処理
     */
    useEffect(() => {
        mode === "addExpiry"
            ? setSelectedActivationDate(convertUtcToLocalDate(selectedLicense.expire_date))
            : setSelectedActivationDate(
                  new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0),
              );
    }, [mode]);

    /**
     * ライセンスタイプ・利用開始日時を変更したときに有効期限を自動計算するフック処理
     */
    useEffect(() => {
        if (selectedLicenseType && selectedActivationDate) {
            // 有効期限を計算してセット
            const exp = calcExpirationDate(selectedLicenseType, selectedActivationDate);
            exp && setSelectedExpirationDate(exp);

            if ([300, 301, 302].includes(selectedLicenseType)) {
                setDisableExpirationDate(true); // 有効期限編集不可（恒久版）
            } else {
                setDisableExpirationDate(false); // 有効期限編集可
            }
        }
    }, [selectedActivationDate, selectedLicenseType]);

    /**
     * ライセンス更新押下時のイベント処理
     * @param event
     */
    const handleUpdateLicense = async () => {
        setErrorMessage("");
        setIsProcessing(true);
        try {
            // 利用開始日と有効期限日時のチェック
            if (!disableExpirationDate) {
                if (
                    selectedExpirationDate &&
                    selectedActivationDate &&
                    selectedExpirationDate?.getTime() < selectedActivationDate?.getTime()
                ) {
                    setErrorMessage("有効期限が利用開始日時より前になっています。");
                    return;
                }
            }

            // 商品情報チェック
            if (selectedProduct == null) {
                setErrorMessage("更新対象の商品情報が見つかりませんでした。");
                return;
            }

            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }

            // [API]Checkout API
            const updateResult: PaymentHistoryWithLicenses = await checkoutWithoutPayment(
                idToken,
                selectedProduct.product_id,
                selectedUser.email,
                1, // 現状は1固定
                mode === "resetStart" ? selectedActivationDate?.toISOString() : undefined, // 利用開始日を再設定する場合のみ指定
                selectedExpirationDate?.toISOString(),
                selectedLicenseId,
            );
            setUpdatedLicense(updateResult);
            setUpdateCompleted(true);
        } catch (error: any) {
            setErrorMessage(t("ErrorMessage.systemErrorMessage"));
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title="ライセンス更新 | DF WebService" />
            <ScrollToTop />
            {/* マスターユーザーのみ閲覧可 */}
            <OnlyMasterUserAuthorized />
            <DFNavbar bottomPadding={true} />
            <div className="flex-grow items-center justify-center px-6 py-8 pt-20">
                <h1 className="h1-common py-3">ライセンス更新</h1>

                {loading && (
                    // ローディング
                    <div className="flex flex-col items-center justify-center p-10">
                        <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-lime-800"></div>
                        <p className="p-3 font-bold text-lime-800">前回購入情報を確認中...</p>
                    </div>
                )}

                {/* エラーメッセージ（上部） */}
                {errorMessageTop && (
                    <div className="flex items-center justify-center py-2">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessageTop.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                {!loading && (
                    <div className="mx-auto max-w-3xl">
                        <div className="m-2 grid grid-cols-1 items-center justify-center gap-4 pb-6 pt-2 sm:grid-cols-3">
                            {/* 製品タイプ */}
                            <div className="col-span-1 py-1">
                                <div className="py-2 text-sm font-bold text-gray-800">
                                    製品タイプ<span className="pl-2 text-xs text-gray-500">*変更不可</span>
                                </div>
                                <select
                                    id="selectProductType"
                                    value={selectedProduct?.product_type_id}
                                    className="w-[180px] rounded-md border border-gray-300 bg-gray-100 p-2 text-sm"
                                    disabled={true} // 無効化
                                >
                                    {productTypeOptions.map((p) => (
                                        <option key={p.productTypeId} value={p.productTypeId}>
                                            {p.productTypeName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* ライセンスタイプ */}
                            <div className="col-span-1 py-1">
                                <div className="py-2 text-sm font-bold text-gray-800">
                                    ライセンスタイプ<span className="pl-2 text-xs text-gray-500">*変更不可</span>
                                </div>
                                <select
                                    id="selectLicenseType"
                                    value={selectedProduct?.license_type_id}
                                    className="w-[180px] rounded-md border border-gray-300 bg-gray-100 p-2 text-sm"
                                    disabled={true} // 無効化
                                >
                                    {licenseOptions.map((l) => (
                                        <option key={l.licenseTypeId} value={l.licenseTypeId}>
                                            {l.labelName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* ライセンスID */}
                            <div className="col-span-1 py-1">
                                <div className="py-2 text-sm font-bold text-gray-800">
                                    ライセンスID<span className="pl-2 text-xs text-gray-500">*変更不可</span>
                                </div>
                                <select
                                    id="selectLicenseId"
                                    value={selectedProduct?.license_type_id}
                                    className="w-[180px] rounded-md border border-gray-300 bg-gray-100 p-2 text-sm"
                                    disabled={true} // 無効化
                                >
                                    <option key={selectedLicense.license_id} value={selectedLicense.license_id}>
                                        {selectedLicense.license_id}
                                    </option>
                                </select>
                            </div>
                        </div>

                        {/* 現在の有効期限に加算 */}
                        <div className="m-2 grid grid-cols-1 items-center justify-center gap-x-4 pb-3 sm:grid-cols-3">
                            <div className="col-span-3 py-1">
                                <input
                                    type="radio"
                                    id="addExpiry"
                                    name="mode"
                                    value="addExpiry"
                                    checked={mode === "addExpiry"}
                                    onChange={() => setMode("addExpiry")}
                                    className="mr-2"
                                    disabled={updateCompleted}
                                />
                                <label htmlFor="addExpiry" className="text-sm font-bold text-gray-800">
                                    現在の有効期限に加算
                                </label>
                            </div>

                            {/* 現在の有効期限日時 */}
                            <div className="col-span-1 py-1 pl-4">
                                <div
                                    className={`${
                                        mode === "addExpiry" ? "text-gray-800" : "text-gray-300"
                                    } pb-1 text-sm`}
                                >
                                    現在の有効期限日時<span className="pl-2 text-xs">*変更不可</span>
                                </div>
                                <div
                                    className={`${
                                        mode === "addExpiry" ? "text-gray-800" : "text-gray-300"
                                    } mb-1 w-[170px] rounded-lg border border-gray-300 bg-gray-100 p-2 text-sm`}
                                >
                                    {formatUtcToLocalDateTime(selectedLicense.expire_date)}
                                </div>
                            </div>

                            {/* 更新後の有効期限日時 */}
                            <div className="col-span-1 py-1 pl-4">
                                <div
                                    className={`${
                                        mode === "addExpiry" ? "text-gray-800" : "text-gray-300"
                                    } pb-1 text-sm`}
                                >
                                    更新後の有効期限日時
                                </div>
                                <MyDatePicker
                                    selectedDate={selectedExpirationDate}
                                    setDate={setSelectedExpirationDate}
                                    placeholderText="-"
                                    disabled={mode !== "addExpiry" || updateCompleted}
                                />
                            </div>
                            <div className="col-span-1 py-1"></div>
                        </div>

                        {/* 利用開始日を再設定 */}
                        <div className="m-2 grid grid-cols-1 items-center justify-center gap-x-4 pb-6 sm:grid-cols-3">
                            <div className="col-span-3 flex items-center py-1">
                                <input
                                    type="radio"
                                    id="resetStart"
                                    name="mode"
                                    value="resetStart"
                                    checked={mode === "resetStart"}
                                    onChange={() => setMode("resetStart")}
                                    className="mr-2"
                                    disabled={updateCompleted}
                                />
                                <label htmlFor="resetStart" className="text-sm font-bold text-gray-800">
                                    利用開始日を再設定
                                </label>
                            </div>

                            {/* 利用開始日時 */}
                            <div className="col-span-1 py-1 pl-4">
                                <div
                                    className={`${
                                        mode === "resetStart" ? "text-gray-800" : "text-gray-300"
                                    } pb-1 text-sm`}
                                >
                                    利用開始日時
                                </div>
                                <MyDatePicker
                                    selectedDate={selectedActivationDate}
                                    setDate={setSelectedActivationDate}
                                    placeholderText="-"
                                    disabled={mode !== "resetStart" || updateCompleted}
                                />
                            </div>

                            {/* 有効期限日時 */}
                            <div className="col-span-1 py-1 pl-4">
                                <div
                                    className={`${
                                        mode === "resetStart" ? "text-gray-800" : "text-gray-300"
                                    } pb-1 text-sm`}
                                >
                                    有効期限日時
                                </div>
                                <MyDatePicker
                                    selectedDate={selectedExpirationDate}
                                    setDate={setSelectedExpirationDate}
                                    placeholderText="-"
                                    disabled={mode !== "resetStart" || updateCompleted}
                                />
                            </div>
                            <div className="col-span-1 py-1"></div>

                            <div
                                className={`${
                                    mode === "resetStart" ? "text-gray-700" : "text-gray-300"
                                } col-span-3 py-1 pl-4 text-xs`}
                            >
                                *
                                「利用開始日を再設定」にした場合、ライセンスのステータスを「未アクティベーション」にリセットします。
                                <br />
                                &nbsp;&nbsp;更新後、ユーザーは再アクティベーション操作が必要になります。
                            </div>
                        </div>
                    </div>
                )}
                <hr className="mb-8 mt-2 border-b border-t-2" />

                {/* エラーメッセージ（中段） */}
                {errorMessage && (
                    <div className="flex items-center justify-center pb-4">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                {/* ライセンス更新前の状態 */}
                {!loading && !updateCompleted && (
                    <>
                        {/* 更新内容確認パネル */}
                        <div className="mx-auto flex max-w-2xl items-center justify-center font-bold">
                            <div className="min-w-full rounded-lg border border-gray-200 bg-yellow-50 p-2 shadow">
                                <div className="pl-2 pt-2 text-gray-700">以下の内容でライセンスを更新します</div>

                                <div className="grid grid-cols-1 items-center justify-center py-2 sm:grid-cols-4">
                                    {/* DFアカウント */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">DF アカウント</div>
                                    <div className="col-span-3 py-1">{selectedUser.email}</div>
                                    {/* 商品名 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">製品タイプ</div>
                                    <div className="col-span-3 py-1">
                                        {
                                            productTypeOptions.find(
                                                (option) => option.productTypeId === selectedProductType,
                                            )?.productTypeName
                                        }
                                    </div>
                                    {/* ライセンス */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">ライセンスタイプ</div>
                                    <div className="col-span-3 py-1">
                                        {selectedLicenseType != undefined ? (
                                            licenseOptions.find((l) => l.licenseTypeId == selectedLicenseType)
                                                ?.licenseTypeName
                                        ) : (
                                            <span className="text-gray-500">== 未選択 ==</span>
                                        )}
                                    </div>
                                    {/* 前回有効期限 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">更新前の有効期限</div>
                                    <div className="col-span-3 py-1">
                                        {formatUtcToLocalDateTimeJAFormat(selectedLicense.expire_date)}
                                    </div>

                                    {/* 利用開始日時 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">更新後の利用開始</div>
                                    <div className="col-span-3 py-1">
                                        {selectedActivationDate && formatDateTimeJAFormat(selectedActivationDate)}
                                    </div>
                                    {/* 有効期限日時 */}
                                    {!disableExpirationDate && (
                                        <>
                                            <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                更新後の有効期限
                                            </div>
                                            <div className="t col-span-3 py-1">
                                                {selectedExpirationDate &&
                                                    formatDateTimeJAFormat(selectedExpirationDate)}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ライセンスを更新 */}
                        <div className="mt-4 flex items-center justify-evenly">
                            <button
                                type="button"
                                onClick={handleUpdateLicense}
                                className={`${
                                    isProcessing || selectedLicenseType == undefined
                                        ? "btn-primary-disabled"
                                        : "btn-primary"
                                } w-[200px] py-2`}
                                disabled={isProcessing || selectedLicenseType == undefined}
                            >
                                {isProcessing ? (
                                    <div className="flex items-center justify-center">
                                        <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                        <label className="">Processing...</label>
                                    </div>
                                ) : (
                                    "ライセンスを更新"
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* ライセンス発行完了状態 */}
                {!loading && updateCompleted && (
                    <>
                        {/* 発行結果表示パネル */}
                        <div className="mx-auto flex max-w-2xl items-center justify-center font-bold">
                            <div className="min-w-full rounded-lg border border-gray-200 bg-green-50 p-2 shadow">
                                <div className="pl-2 pt-2 text-green-600">ライセンスの更新が完了しました</div>

                                <div className="grid grid-cols-4 items-center justify-center py-2">
                                    {/* DFアカウント */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">DF アカウント</div>
                                    <div className="col-span-3 py-1">{updatedLicense?.p_email}</div>
                                    {/* 氏名 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">氏名</div>
                                    <div className="col-span-3 py-1">{updatedLicense?.p_name}</div>
                                    {/* 所属 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">所属</div>
                                    <div className="col-span-3 py-1">{updatedLicense?.p_organization}</div>
                                    {/* 商品名 */}
                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">商品名</div>
                                    <div className="col-span-3 py-1">
                                        {i18n.language === "ja"
                                            ? updatedLicense?.p_product_type_name.split("%%")[0]
                                            : updatedLicense?.p_product_type_name.split("%%")[1]}
                                    </div>
                                    {updatedLicense?.licenses.map((license, index) => (
                                        <React.Fragment key={index}>
                                            <div className="col-span-4 py-1 pl-4 text-sm text-gray-500">
                                                == ライセンス情報 {index + 1} ==
                                            </div>
                                            {/* ライセンスキー */}
                                            <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                ライセンスキー
                                            </div>
                                            <div className="col-span-3 py-1">{license.license_key}</div>
                                            {/* 更新後の利用開始 */}
                                            <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                更新後の利用開始
                                            </div>
                                            <div className="col-span-3 py-1">
                                                {selectedActivationDate && formatDateTime(selectedActivationDate)}
                                            </div>
                                            {/* 更新後の有効期限 */}
                                            {!license.permanent_flg && (
                                                <>
                                                    <div className="col-span-1 py-1 pl-4 text-sm text-gray-500">
                                                        更新後の有効期限
                                                    </div>
                                                    <div className="col-span-3 py-1">
                                                        {formatUtcToLocalDateTime(license.expire_date)}
                                                    </div>
                                                </>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ライセンス確認画面へ戻る */}
                <div className="flex items-center justify-center">
                    <div className="grid max-w-md grid-cols-2 gap-6 py-6 sm:m-2">
                        {/* ライセンス確認画面へ戻る */}
                        <div className="col-span-2 mx-auto max-w-xl items-center justify-evenly sm:col-span-1">
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(MANAGEMENT_USER_LICENSE_SEARCH, { state: { selectedUser: selectedUser } })
                                }
                                className="btn-secondary mx-2 w-[200px]"
                            >
                                ライセンス確認画面へ戻る
                            </button>
                        </div>
                        {/* マイメニューへ戻る */}
                        <div className="col-span-2 mx-auto max-w-xl items-center justify-evenly sm:col-span-1">
                            <button
                                type="button"
                                onClick={() => navigate(MYPAGE_ROUTE)}
                                className="btn-secondary mx-2 w-[200px]"
                            >
                                マイメニューへ戻る
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};
export default ManagementUserLicenseUpdate;
